/**
 * Rutina, gimnasio y paquete: lo único que la app nombraba y no se podía editar.
 *
 * Hasta acá las tres cosas se definían UNA vez, en la bienvenida, y no se volvían a ver nunca:
 * cinco textos mandaban a "Ajustes → Rutina" y esa sección no existía. Cuando el paquete vencía,
 * cuando el gimnasio cambiaba su día de cierre o cuando había que sacar un ejercicio, no había
 * salida — y `guardarPlan` estaba escrita en `acciones.ts` sin un solo llamador.
 *
 * Es lógica COMPARTIDA por las dos variantes de Ajustes: acá no hay JSX (regla 1 del proyecto).
 * La v1 agrega, quita y renombra; no reordena (deuda anotada en `docs/flujo.md` §7).
 */

import { useCallback, useMemo, useState } from 'react';
import {
  CATALOGO_BASE,
  addDias,
  type Ejercicio,
  type Fecha,
  type Plan,
  type RutinaDia,
  type RutinaEjercicio,
} from '@mg/core';

import { pedir } from '../datos/api.ts';
import { db, guardarLocal } from '../datos/db.ts';
import { guardarPlan } from './acciones.ts';
import { useDatos } from './useDatos.ts';
import { useMarcaGuardado } from './useAjustes.ts';

/** Lo mismo que siembra la bienvenida: un ejercicio nuevo entra con 3 series de 10 a 12. */
export const SERIES_OBJETIVO = 3;
export const REPS_MIN = 10;
export const REPS_MAX = 12;

export const DIAS_SEMANA = [
  { n: 1, etiqueta: 'Lun' },
  { n: 2, etiqueta: 'Mar' },
  { n: 3, etiqueta: 'Mié' },
  { n: 4, etiqueta: 'Jue' },
  { n: 5, etiqueta: 'Vie' },
  { n: 6, etiqueta: 'Sáb' },
  { n: 0, etiqueta: 'Dom' },
];

const nuevoId = (p: string): string => `${p}-${crypto.randomUUID()}`;

/** Un día de la rutina con sus filas, incluso las que el catálogo local no sabe resolver. */
export interface DiaDeRutina {
  dia: RutinaDia;
  filas: { fila: RutinaEjercicio; ejercicio: Ejercicio | null }[];
}

/**
 * La bienvenida escribe las notas del gimnasio a partir de sus días de cierre. Se regeneran sólo
 * si siguen siendo ese texto: una nota que escribió una persona no se pisa.
 */
function notasDeCierre(previas: string, dias: number[]): string {
  const generadas =
    dias.length > 0
      ? `Cierra: ${dias.map((d) => DIAS_SEMANA.find((x) => x.n === d)?.etiqueta).join(', ')}`
      : '';
  return previas.trim() === '' || previas.startsWith('Cierra:') ? generadas : previas;
}

const mismosDias = (a: number[], b: number[]): boolean => {
  if (a.length !== b.length) return false;
  const uno = [...a].sort((p, q) => p - q);
  const otro = [...b].sort((p, q) => p - q);
  return uno.every((n, i) => n === otro[i]);
};

export function useRutina() {
  const datos = useDatos();
  const { marca, marcar } = useMarcaGuardado();

  const [errorRutina, setErrorRutina] = useState<string | null>(null);
  const [errorGimnasio, setErrorGimnasio] = useState<string | null>(null);
  const [errorPlan, setErrorPlan] = useState<string | null>(null);
  const [subiendoGimnasio, setSubiendoGimnasio] = useState(false);

  // Sin haber sincronizado nunca, el catálogo local puede estar vacío: se cae al de core, que es
  // el mismo que siembra la API. Es lo que hace que el editor funcione sin un pull previo.
  const catalogo: Ejercicio[] = datos.ejercicios.length > 0 ? datos.ejercicios : CATALOGO_BASE;
  const catalogoPorId = useMemo(() => new Map(catalogo.map((e) => [e.id, e])), [catalogo]);

  // --- Rutina ---------------------------------------------------------------

  const dias = useMemo<DiaDeRutina[]>(
    () =>
      datos.rutinaDias.map((dia) => ({
        dia,
        filas: datos.rutinaEjercicios
          .filter((re) => re.rutina_dia_id === dia.id)
          .sort((a, b) => a.orden - b.orden)
          // El ejercicio puede no resolverse (una fila vieja, un catálogo a medio bajar). Se
          // muestra igual: es justo la fila que hay que arreglar, esconderla no ayuda.
          .map((fila) => ({ fila, ejercicio: catalogoPorId.get(fila.ejercicio_id) ?? null })),
      })),
    [datos.rutinaDias, datos.rutinaEjercicios, catalogoPorId],
  );

  const totalEjercicios = dias.reduce((n, d) => n + d.filas.length, 0);

  const disponiblesPara = useCallback(
    (diaId: string): Ejercicio[] => {
      const puestos = new Set(
        datos.rutinaEjercicios.filter((re) => re.rutina_dia_id === diaId).map((re) => re.ejercicio_id),
      );
      return catalogo.filter((e) => !puestos.has(e.id));
    },
    [datos.rutinaEjercicios, catalogo],
  );

  const correr = useCallback(async (accion: () => Promise<void>, siFalla: string): Promise<void> => {
    setErrorRutina(null);
    try {
      await accion();
    } catch (e) {
      setErrorRutina(e instanceof Error ? e.message : siFalla);
    }
  }, []);

  const agregarDia = useCallback(
    (): Promise<void> =>
      correr(async () => {
        if (!datos.perfil) return;
        // Una cuenta que llegó acá sin rutina activa no puede quedarse sin dónde poner el día.
        let rutinaId = datos.rutinaActiva?.id ?? null;
        if (!rutinaId) {
          rutinaId = nuevoId('r');
          await guardarLocal('rutinas', [
            { id: rutinaId, user_id: datos.perfil.id, nombre: 'Mi rutina', activa: true },
          ]);
        }
        const orden = datos.rutinaDias.reduce((max, d) => Math.max(max, d.orden), 0) + 1;
        await guardarLocal('rutina_dias', [
          { id: nuevoId('rd'), rutina_id: rutinaId, orden, nombre: `Día ${orden}` },
        ]);
        marcar('dias');
      }, 'No se pudo agregar el día.'),
    [correr, datos.perfil, datos.rutinaActiva, datos.rutinaDias, marcar],
  );

  const renombrarDia = useCallback(
    (dia: RutinaDia, nombre: string): Promise<void> =>
      correr(async () => {
        const limpio = nombre.trim();
        if (limpio === '' || limpio === dia.nombre) return;
        await guardarLocal('rutina_dias', [{ ...dia, nombre: limpio }]);
        marcar(`dia:${dia.id}`);
      }, 'No se pudo renombrar el día.'),
    [correr, marcar],
  );

  /**
   * Quitar un día se lleva sus filas de ejercicios, y nada más: las series ya registradas apuntan
   * a la sesión, no al día de rutina, así que el historial queda intacto.
   */
  const quitarDia = useCallback(
    (dia: RutinaDia): Promise<void> =>
      correr(async () => {
        await db.transaction('rw', db.rutinaDias, db.rutinaEjercicios, db.sucios, async () => {
          const filas = await db.rutinaEjercicios.where('rutina_dia_id').equals(dia.id).toArray();
          await db.rutinaEjercicios.bulkDelete(filas.map((f) => f.id));
          await db.sucios.bulkDelete(filas.map((f) => `rutina_ejercicios:${f.id}`));
          await db.rutinaDias.delete(dia.id);
          await db.sucios.delete(`rutina_dias:${dia.id}`);
        });
        marcar('dias');
      }, 'No se pudo quitar el día.'),
    [correr, marcar],
  );

  const agregarEjercicio = useCallback(
    (diaId: string, ejercicioId: string): Promise<void> =>
      correr(async () => {
        const enElDia = datos.rutinaEjercicios.filter((re) => re.rutina_dia_id === diaId);
        if (enElDia.some((re) => re.ejercicio_id === ejercicioId)) return;
        const orden = enElDia.reduce((max, re) => Math.max(max, re.orden), 0) + 1;
        await guardarLocal('rutina_ejercicios', [
          {
            id: nuevoId('re'),
            rutina_dia_id: diaId,
            ejercicio_id: ejercicioId,
            orden,
            series_objetivo: SERIES_OBJETIVO,
            reps_objetivo_min: REPS_MIN,
            reps_objetivo_max: REPS_MAX,
          },
        ]);
        marcar(`dia:${diaId}`);
      }, 'No se pudo agregar el ejercicio.'),
    [correr, datos.rutinaEjercicios, marcar],
  );

  const quitarEjercicio = useCallback(
    (fila: RutinaEjercicio): Promise<void> =>
      correr(async () => {
        await db.transaction('rw', db.rutinaEjercicios, db.sucios, async () => {
          await db.rutinaEjercicios.delete(fila.id);
          await db.sucios.delete(`rutina_ejercicios:${fila.id}`);
        });
        marcar(`dia:${fila.rutina_dia_id}`);
      }, 'No se pudo quitar el ejercicio.'),
    [correr, marcar],
  );

  // --- Gimnasio -------------------------------------------------------------

  const gimnasio = datos.gimnasioActivo;
  const [cierreEditado, setCierreEditado] = useState<number[] | null>(null);
  const diasCierre = cierreEditado ?? gimnasio?.dias_cierre_semanal ?? [];

  const alternarCierre = useCallback(
    (n: number) => {
      setCierreEditado((prev) => {
        const base = prev ?? gimnasio?.dias_cierre_semanal ?? [];
        return base.includes(n) ? base.filter((x) => x !== n) : [...base, n].sort((a, b) => a - b);
      });
    },
    [gimnasio],
  );

  const hayCambiosGimnasio =
    cierreEditado !== null && !mismosDias(diasCierre, gimnasio?.dias_cierre_semanal ?? []);

  /**
   * El gimnasio es del catálogo COMPARTIDO (§2): sube al servidor o no pasa nada. Guardarlo en
   * local y prometer que "sube después" haría creer que los demás ya lo ven, y no es cierto.
   */
  const guardarGimnasio = useCallback(async (): Promise<void> => {
    if (!gimnasio) return;
    setSubiendoGimnasio(true);
    setErrorGimnasio(null);
    try {
      const dias = [...diasCierre].sort((a, b) => a - b);
      const actualizado = {
        ...gimnasio,
        dias_cierre_semanal: dias,
        notas: notasDeCierre(gimnasio.notas, dias),
      };
      await pedir('/catalogo/gimnasios', { metodo: 'POST', cuerpo: { gimnasios: [actualizado] } });
      await db.gimnasios.put(actualizado);
      setCierreEditado(null);
      marcar('gimnasio');
    } catch (e) {
      setErrorGimnasio(e instanceof Error ? e.message : 'No se pudo subir el cambio.');
    } finally {
      setSubiendoGimnasio(false);
    }
  }, [gimnasio, diasCierre, marcar]);

  // --- Paquete de sesiones --------------------------------------------------

  const plan = datos.planActivo;
  const [sesionesEditadas, setSesionesEditadas] = useState<string | null>(null);
  const [vencimientoEditado, setVencimientoEditado] = useState<Fecha | null>(null);

  const sesionesTotales = sesionesEditadas ?? (plan ? String(plan.sesiones_totales) : '12');
  const vencimiento = vencimientoEditado ?? plan?.fecha_vencimiento ?? addDias(datos.hoy, 30);

  const sesionesNum = Number(sesionesTotales);
  const sesionesOk = Number.isFinite(sesionesNum) && sesionesNum > 0;
  const planValido = sesionesOk && vencimiento >= datos.hoy && (plan !== null || gimnasio !== null);

  /** Qué falta, para que el botón deshabilitado no sea una pared. */
  const queFaltaPlan = useMemo((): string | null => {
    if (planValido) return null;
    if (!sesionesOk) return 'Poné cuántas sesiones trae el paquete.';
    if (vencimiento < datos.hoy) return 'El vencimiento tiene que ser hoy o una fecha futura.';
    return 'Falta cargar un gimnasio: un paquete siempre es de un gimnasio.';
  }, [planValido, sesionesOk, vencimiento, datos.hoy]);

  const guardarPaquete = useCallback(async (): Promise<void> => {
    if (!datos.perfil || !planValido) return;
    setErrorPlan(null);
    try {
      // Tipado como `Plan` antes de pasarlo: `guardarPlan` recibe la forma mínima de `guardarLocal`
      // (`{ id: string }`) y un literal suelto perdería el chequeo de campos del plan.
      if (plan) {
        const editado: Plan = { ...plan, sesiones_totales: sesionesNum, fecha_vencimiento: vencimiento };
        await guardarPlan(editado);
      } else {
        if (!gimnasio) return;
        const nuevo: Plan = {
          id: nuevoId('p'),
          user_id: datos.perfil.id,
          gimnasio_id: gimnasio.id,
          sesiones_totales: sesionesNum,
          fecha_inicio: datos.hoy,
          fecha_vencimiento: vencimiento,
        };
        await guardarPlan(nuevo);
      }
      setSesionesEditadas(null);
      setVencimientoEditado(null);
      marcar('plan');
    } catch (e) {
      setErrorPlan(e instanceof Error ? e.message : 'No se pudo guardar el paquete.');
    }
  }, [datos.perfil, datos.hoy, planValido, plan, gimnasio, sesionesNum, vencimiento, marcar]);

  return {
    datos,
    marca,

    rutina: datos.rutinaActiva,
    dias,
    totalEjercicios,
    catalogo,
    catalogoPorId,
    disponiblesPara,
    agregarDia,
    renombrarDia,
    quitarDia,
    agregarEjercicio,
    quitarEjercicio,
    errorRutina,

    gimnasio,
    diasSemana: DIAS_SEMANA,
    diasCierre,
    alternarCierre,
    hayCambiosGimnasio,
    guardarGimnasio,
    subiendoGimnasio,
    errorGimnasio,

    plan,
    sesionesTotales,
    setSesionesTotales: (v: string) => setSesionesEditadas(v),
    vencimiento,
    setVencimiento: (v: Fecha) => setVencimientoEditado(v),
    planValido,
    queFaltaPlan,
    guardarPaquete,
    errorPlan,
  };
}
